import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getAuditLogs } from '../../services/auditService';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import './AdminPages.css';

const ITEMS_PER_PAGE = 20;

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filter states
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    adminEmail: '',
    questionKey: '',
    startDate: '',
    endDate: ''
  });

  // Unique values for filter dropdowns
  const [uniqueAdmins, setUniqueAdmins] = useState([]);
  const [expandedLogId, setExpandedLogId] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await getAuditLogs({ limit: 500 });
      setLogs(data);
      setFilteredLogs(data);
      
      // Extract unique admin emails
      const admins = [...new Set(data.map(log => log.adminEmail).filter(Boolean))];
      setUniqueAdmins(admins);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let result = [...logs];

    // Filter by action
    if (filters.action) {
      result = result.filter(log => log.action === filters.action);
    }

    // Filter by entity type
    if (filters.entityType) {
      result = result.filter(log => log.entityType === filters.entityType);
    }

    // Filter by admin email
    if (filters.adminEmail) {
      result = result.filter(log => 
        log.adminEmail?.toLowerCase().includes(filters.adminEmail.toLowerCase())
      );
    }

    // Filter by question key (search in entityId or before/after data)
    if (filters.questionKey) {
      const searchKey = filters.questionKey.toLowerCase();
      result = result.filter(log => 
        log.entityId?.toLowerCase().includes(searchKey) ||
        log.before?.key?.toLowerCase().includes(searchKey) ||
        log.after?.key?.toLowerCase().includes(searchKey)
      );
    }

    // Filter by date range
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      result = result.filter(log => {
        const logDate = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
        return logDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      result = result.filter(log => {
        const logDate = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
        return logDate <= endDate;
      });
    }

    setFilteredLogs(result);
    setCurrentPage(1);
  }, [logs, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entityType: '',
      adminEmail: '',
      questionKey: '',
      startDate: '',
      endDate: ''
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'CREATE': return 'action-badge create';
      case 'UPDATE': return 'action-badge update';
      case 'DELETE': return 'action-badge delete';
      case 'PUBLISH': return 'action-badge publish';
      default: return 'action-badge';
    }
  };

  const toggleLogExpansion = (logId) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderBeforeAfter = (log) => {
    if (!log.before && !log.after) return null;

    return (
      <motion.div
        className="log-details"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2 }}
      >
        {log.changes && log.changes.length > 0 && (
          <div className="changes-summary">
            <strong>Changed Fields:</strong> {log.changes.join(', ')}
          </div>
        )}
        
        <div className="before-after-container">
          {log.before && (
            <div className="before-section">
              <h4>Before</h4>
              <pre>{JSON.stringify(log.before, null, 2)}</pre>
            </div>
          )}
          {log.after && (
            <div className="after-section">
              <h4>After</h4>
              <pre>{JSON.stringify(log.after, null, 2)}</pre>
            </div>
          )}
        </div>
      </motion.div>
    );
  };


  const renderLogRow = (log) => (
    <motion.div
      key={log.id}
      className={`audit-log-row ${expandedLogId === log.id ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="log-main" onClick={() => toggleLogExpansion(log.id)}>
        <div className="log-cell action-cell">
          <span className={getActionBadgeClass(log.action)}>{log.action}</span>
        </div>
        <div className="log-cell entity-cell">
          <span className="entity-type">{log.entityType}</span>
          <span className="entity-id">{log.entityId}</span>
        </div>
        <div className="log-cell admin-cell">
          {log.adminEmail || 'Unknown'}
        </div>
        <div className="log-cell timestamp-cell">
          {formatDate(log.timestamp)}
        </div>
        <div className="log-cell expand-cell">
          <span className="expand-icon">{expandedLogId === log.id ? '▼' : '▶'}</span>
        </div>
      </div>
      
      {expandedLogId === log.id && renderBeforeAfter(log)}
    </motion.div>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination">
        <Button
          variant="secondary"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          First
        </Button>
        <Button
          variant="secondary"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          Prev
        </Button>
        
        {pages.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'primary' : 'secondary'}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </Button>
        ))}
        
        <Button
          variant="secondary"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
        <Button
          variant="secondary"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </Button>
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="admin-audit-logs">
      <div className="admin-header">
        <h1>Audit Logs</h1>
        <p>Track all administrative actions and changes</p>
      </div>

      <div className="audit-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Action</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="PUBLISH">Publish</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Entity Type</label>
            <select
              value={filters.entityType}
              onChange={(e) => handleFilterChange('entityType', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="question">Question</option>
              <option value="version">Version</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Admin</label>
            <select
              value={filters.adminEmail}
              onChange={(e) => handleFilterChange('adminEmail', e.target.value)}
            >
              <option value="">All Admins</option>
              {uniqueAdmins.map(admin => (
                <option key={admin} value={admin}>{admin}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Question Key</label>
            <input
              type="text"
              placeholder="Search by key..."
              value={filters.questionKey}
              onChange={(e) => handleFilterChange('questionKey', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
            <Button variant="primary" onClick={fetchLogs}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="audit-summary">
        <span>Showing {filteredLogs.length} of {logs.length} logs</span>
        {filteredLogs.length !== logs.length && (
          <span className="filter-active">(filters active)</span>
        )}
      </div>

      {filteredLogs.length === 0 ? (
        <div className="no-logs">
          <p>No audit logs found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="audit-table">
            <div className="audit-table-header">
              <div className="header-cell action-cell">Action</div>
              <div className="header-cell entity-cell">Entity</div>
              <div className="header-cell admin-cell">Admin</div>
              <div className="header-cell timestamp-cell">Timestamp</div>
              <div className="header-cell expand-cell"></div>
            </div>
            <div className="audit-table-body">
              {paginatedLogs.map(log => renderLogRow(log))}
            </div>
          </div>

          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default AdminAuditLogs;
